<div class="bk-page camps">
  <!-- feedback -->
  <feedback-card feedBackType="error" [message]="errorMessage || campaignsService.errorMsg" />
  <feedback-card feedBackType="success" [message]="successMessage" />

  <!-- The page, how campaigns stand right now, and the one thing to do next -->
  <header class="bk-page__head">
    <div class="bk-page__intro">
      <p class="bk-eyebrow">SMS</p>
      <h1 class="bk-page__title">Campaigns</h1>

      @if (countsLoading && !statusCounts) {
        <span class="bk-skel camps__counts-skel" aria-hidden="true"></span>
      } @else if (statusCounts && countTotal > 0) {
        <!-- Gaps, not dots, separate the figures; each one filters the list -->
        <ul class="camps__counts" aria-label="Filter campaigns by status">
          @for (item of visibleCounts; track item.group.key) {
            <li>
              <button
                type="button"
                class="camps__count"
                [class.is-active]="statusValue === item.group.key"
                [attr.aria-pressed]="statusValue === item.group.key"
                (click)="toggleStatus(item.group.key)"
              >
                <span class="camps__dot" [attr.data-tone]="item.group.dot" aria-hidden="true"></span>
                <strong>{{ formatCount(item.count) }}</strong>
                {{ item.group.noun(item.count) }}
              </button>
            </li>
          }
        </ul>
      } @else {
        <p class="bk-page__sub">
          Each campaign sends one or more messages to the same audience on a schedule.
        </p>
      }
    </div>

    <div class="bk-page__actions">
      <ng-container *hasPermission="{ category: 'CAMPAIGN_MANAGEMENT', permission: 'create-campaigns' }">
        <button type="button" class="bk-btn bk-btn--primary" (click)="navigateToCreate()">
          <span class="bk-sym" aria-hidden="true">add</span>
          New campaign
        </button>
      </ng-container>
    </div>
  </header>

  <!-- Search and filters -->
  <div class="bk-toolbar camps__toolbar">
    <app-search-input
      class="camps__search"
      placeholder="Search campaigns by name"
      [ngModel]="searchTerm"
      (valueChange)="updateSearchTerm($event)"
    />

    <app-status-filter
      [options]="statusOptions"
      [selectedValue]="statusValue"
      allLabel="All statuses"
      (selectionChange)="updateStatusCriteria($any($event))"
    />

    <app-sort-dropdown
      panelAlign="right"
      [groups]="sortGroups"
      [selectedValue]="sortValue"
      (selectionChange)="updateSortCriteria($any($event))"
    />

    <!-- Always in the page, so screen readers hear each new total -->
    <p class="camps__summary" aria-live="polite">{{ loading ? "" : pageSummary }}</p>
  </div>

  <!-- Campaigns -->
  <div class="bk-panel camps__panel">
    @if (loadError && !loading) {
      <app-empty-state
        icon="error"
        message="Campaigns didn’t load"
        [hint]="loadError + ' Try again in a moment.'"
        actionLabel="Try again"
        actionIcon="refresh"
        (action)="reload()"
      />
    } @else if (!loading && campaigns.length === 0) {
      @if (isFiltered) {
        <app-empty-state
          icon="campaigns"
          [query]="searchTerm"
          [filtered]="!!statusValue"
          (clear)="clearFilters()"
        />
      } @else {
        <app-empty-state
          icon="campaigns"
          message="No campaigns yet"
          hint="A campaign sends one or more messages to the same audience on a schedule. Create one to get started."
          actionLabel="New campaign"
          actionIcon="add"
          (action)="navigateToCreate()"
        />
      }
    } @else {
      <app-campaign-table
        [tableItems]="campaigns"
        [loading]="loading"
        [duplicatingId]="duplicatingId"
        (showDelete)="showDeletePrompt($event)"
        (duplicate)="duplicateCampaign($event)"
      />
    }
  </div>

  <!-- Pagination -->
  @if (campaigns.length > 0 || campaignsPagination.pageNumber > 0) {
    <div class="camps__pagination">
      <app-pagination
        [loading]="loading"
        (changePage)="updateCampaignsPage($event)"
        [currentPage]="campaignsPagination.pageNumber"
        (onNext)="goToNextPage()"
        (onPrev)="goToPreviousPage()"
        [totalPages]="campaignsPagination.totalPages"
        [pageSize]="campaignsPagination.pageSize"
        [totalItems]="campaignsPagination.totalElements"
        (pageSizeChange)="updateCampaignsPageSize($event)"
      />
    </div>
  }
</div>

<!-- delete -->
@if (showDelete) {
  <app-prompt-card
    [openModal]="showDelete"
    title="Delete this campaign?"
    buttonText="Delete campaign"
    closeText="Keep campaign"
    impactIcon="warning"
    impact="This can’t be undone."
    (onClose)="showDelete = false"
    (onButtonClick)="deleteCampaign()"
    [loading]="campaignsService.isLoading"
  >
    <p class="camps__prompt">
      @if (deleteTarget?.name) {
        “{{ deleteTarget?.name }}” is removed from your campaigns.
      } @else {
        The campaign is removed from your campaigns.
      }
      Messages that already went out are not recalled.
    </p>
  </app-prompt-card>
}
